import { RootState, store } from 'modules/Editor/store';
import { selectWidgetLayerOrder, selectWidgets } from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { WidgetId } from 'types/idTypes';
import { LineWidgetData, LineWidgetStyle, Side } from 'widgets/Lines/LineWidget/LineWidget.types';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

// Map of sides and their adjacents to check when computing port line style
const SIDES_TO_CHECK = {
  [Side.NORTH]: [Side.CENTER, Side.WEST, Side.EAST, Side.SOUTH],
  [Side.WEST]: [Side.CENTER, Side.NORTH, Side.SOUTH, Side.EAST],
  [Side.SOUTH]: [Side.CENTER, Side.WEST, Side.EAST, Side.NORTH],
  [Side.EAST]: [Side.CENTER, Side.NORTH, Side.SOUTH, Side.WEST],
  [Side.CENTER]: [Side.EAST, Side.SOUTH, Side.WEST, Side.NORTH],
};

/**
 * Computes the line style for the given widget and side.
 * Logic:
 * - First tries to find a line attached to the same side
 * - If not found, will check the other attached lines in order defined by SIDES_TO_CHECK
 *
 * @param {WidgetId | undefined} widgetId - The ID of the widget
 * @param {Side} side - The side for which to compute the line style
 * @returns {LineWidgetStyle | null} The computed line style or null if no style is found
 */
export const computePortLineStyle = (widgetId: WidgetId | undefined, side: Side): LineWidgetStyle | null => {
  if (!widgetId) return null;

  const connectedLines = lineConnectionCache.getAttachedLines(widgetId);
  if (!connectedLines || !connectedLines.length) return null;

  // check the connected lines
  // if there is one connected to the same side, then use that line's style
  // otherwise, try to find an adjacent line
  // if no adjacent line, will use whatever is available
  const widgets = selectWidgets(store.getState() as RootState);
  let style = null;
  let foundStyle = false;

  // Order to check the sides if no line is found on the same side
  const sideOrderToCheck = SIDES_TO_CHECK[side];

  // Map of side to line widgets
  const lineWidgetSideMap: { [key in Side]?: LineWidgetData[] } = {
    [Side.NORTH]: [],
    [Side.EAST]: [],
    [Side.SOUTH]: [],
    [Side.WEST]: [],
    [Side.CENTER]: [],
  };

  // Loop through attached lines to find a line connected to the same side
  for (const lineId of connectedLines) {
    if (foundStyle) {
      break;
    }

    const lineWidget = widgets[lineId] as LineWidgetData;
    if (!lineWidget) {
      continue;
    }

    const { startPort, endPort } = lineWidget;
    if (startPort && startPort.widgetId === widgetId) {
      lineWidgetSideMap[startPort.side]?.push(lineWidget);
      if (startPort.side === side) {
        foundStyle = true;
      }
    }

    if (endPort && endPort.widgetId === widgetId) {
      lineWidgetSideMap[endPort.side]?.push(lineWidget);
      if (endPort.side === side) {
        foundStyle = true;
      }
    }

    if (foundStyle) {
      style = {
        strokeWidth: lineWidget.strokeWidth,
        strokeColor: lineWidget.strokeColor,
        strokeDashType: lineWidget.strokeDashType,
        startArrowStyle: lineWidget.startArrowStyle,
        endArrowStyle: lineWidget.endArrowStyle,
        type: lineWidget.type,
      };
    }
  }

  // Didn't find a line connected to the same side
  // Loop through the lines starting with the adjacent sides
  if (!foundStyle) {
    for (const currentSide of sideOrderToCheck) {
      const lineWidgets = lineWidgetSideMap[currentSide];
      if (!lineWidgets || !lineWidgets.length) {
        continue;
      }

      // Loop through the lines and use the first one found
      for (const lineWidget of lineWidgets) {
        if (!lineWidget) {
          continue;
        }

        foundStyle = true;
        style = {
          strokeWidth: lineWidget.strokeWidth,
          strokeColor: lineWidget.strokeColor,
          strokeDashType: lineWidget.strokeDashType,
          startArrowStyle: lineWidget.startArrowStyle,
          endArrowStyle: lineWidget.endArrowStyle,
          type: lineWidget.type,
        };
        break;
      }

      if (foundStyle) {
        break;
      }
    }
  }

  return style;
};

/**
 * Get the widget ID that is lower in the layer order
 *
 * @param wId1
 * @param wId2
 * @returns WidgetId of widget that is lower in the layer order
 */
export const getLowerLayerWidgetId = (wId1: WidgetId, wId2: WidgetId): WidgetId => {
  const storeState = store.getState() as RootState;
  const activePageId = selectActivePage(storeState);
  const pageLayerOrder = selectWidgetLayerOrder(activePageId)(storeState);

  const w1Index = pageLayerOrder.indexOf(wId1);
  const w2Index = pageLayerOrder.indexOf(wId2);
  return w1Index < w2Index ? wId1 : wId2;
};
